import { ApiClient, TokenAdd } from "@/services/apiClient";
import { ApiResponseImageNoId } from "@/services/images";
import { createPrice, PriceNumericOrEmpty } from "@/services/prices";
import { Uuid } from "@/types";
import * as PostHog from "@/services/analytics/postHog";
import { useVisitorFingerprint } from "@/composables";
import { hasPrice } from "@/services/aiHelpers";
import { ParameterValueSaveData, useParametersApi } from "@/composables/collectionItem/params";
import { CategoryId } from "@/services/collection/categories";
import { setImagesFromAiIdentification } from "@/services/collection/item/gallery";
import { type CollectionItem, createCollectionItem, type CreateCollectionItemData, updateCollectionItem } from "@/services/collection/item";
import { useUser } from "@/composables/user";

export type HistoryListItem = {
  id: Uuid;
  categoryId: CategoryId;
  name: string;
  datetime: string; // ISO 8601 format
  priceFrom: PriceNumericOrEmpty;
  priceTo: PriceNumericOrEmpty;
  images: ApiResponseImageNoId[];
};

type ApiResponse_HistoryList = {
  totalCount: number;
  items: HistoryListItem[];
};

export type HistoryItemDetail = {
  id: Uuid;
  categoryId: CategoryId;
  name: string;
  priceFrom: PriceNumericOrEmpty;
  priceTo: PriceNumericOrEmpty;
  images: ApiResponseImageNoId[];
  description: string | null;
  confidenceScore: number;
  parameters: {
    parameterId: number;
    value: string;
    valueId: number;
  }[];
} & (
  | {
      categoryId: CategoryId.LegoSets;
      catalogueItem: {
        id: number;
        number: string;
        themeName: string | undefined;
        year: number;
        partsCount: number;
        weight: string;
        dimensions: string;
      };
    }
  | {
      categoryId: CategoryId.LegoMinifigures;
      catalogueItem: {
        id: number;
        number: number;
        partsCount: number;
      };
    }
  | {
      categoryId: CategoryId.SportsCards | CategoryId.VinylRecords | CategoryId.BoardGames | CategoryId.Pokemon | CategoryId.Watches;
    }
);

export enum ErrorType {
  RateLimitReached = "rate_limit",
  ContentFilter = "content_filter",
  InsufficientQuota = "insufficient_quota",
  FakeDetected = "product_is_fake",
}

const lastCategoryId = ref<CategoryId>();

export function useAi() {
  const { user } = useUser();
  const fingerprint = useVisitorFingerprint();

  const lookup = async (categoryId: CategoryId, description: string, images: File[]): Promise<HistoryItemDetail> => {
    const requestTimeout = 45000; // ms
    const data = new FormData();
    const url = "api-public/ai";
    data.append("categoryId", categoryId.toString());
    data.append("description", description);
    images.forEach((image) => data.append("images[]", image));

    if (user.value === undefined) {
      const hash = await fingerprint.get();
      data.append("fingerprint", hash);
    }

    return ApiClient.postPublic<HistoryItemDetail>(url, {}, data, TokenAdd.IfAvailable, requestTimeout).then(({ data }) => data);
  };

  const fetchHistoryList = async (page: number, perPage: number) => ApiClient.get<ApiResponse_HistoryList>("api/ai", { page, perPage }).then(({ data }) => data);

  const fetchItem = async (itemId: Uuid) => {
    const url = "api-public/ai";
    return ApiClient.getPublic<HistoryItemDetail>(url, { id: itemId }).then(({ data }) => data);
  };

  const { saveSelectedValue: saveParameterValue } = useParametersApi();

  const createCollectionItemFromIdentification = async (identification: HistoryItemDetail): Promise<CollectionItem> => {
    // 1) Create item itself
    const categoryId = identification.categoryId;
    let creationData: CreateCollectionItemData;
    if (categoryId === CategoryId.LegoSets || categoryId === CategoryId.LegoMinifigures) {
      creationData = {
        categoryId,
        collectibleId: identification.catalogueItem.id,
        set: identification.catalogueItem.id,
        minifigure: identification.catalogueItem.id,
        userCollectionListName: "",
      };
    } else {
      creationData = {
        categoryId,
        customName: identification.name,
        userCollectionListName: "",
      };
    }
    const item = await createCollectionItem(creationData);

    const promises = [];

    // 2) Add parameters
    identification.parameters.map((param) => {
      const saveData: ParameterValueSaveData = {
        itemId: item.id,
        parameterId: param.parameterId,
        value: param.valueId,
        type: "predefined",
      };
      promises.push(saveParameterValue(saveData));
    });

    // 3) Add images
    promises.push(setImagesFromAiIdentification(item, identification));

    // 4) Set price
    if (categoryId === CategoryId.LegoMinifigures) {
      // For minifigs we use database prices.
      // See: https://app.asana.com/1/1205261752347246/task/1210065097967929/comment/1210097210220302?focus=true
      // Therefore we do not save any price here
    } else {
      if (hasPrice(identification)) {
        const priceFrom = identification.priceFrom;
        const priceTo = identification.priceTo;
        const avgPriceAmount = (priceTo.price + priceFrom.price) / 2;
        const currentPrice = createPrice(avgPriceAmount, priceFrom.currencyId);
        promises.push(updateCollectionItem(item.id, { currentPrice }));
      }
    }

    // 5) Set identification origin
    const aiIdentificationSourceId = identification.id;
    promises.push(updateCollectionItem(item.id, { aiIdentificationSourceId }));

    await Promise.all(promises);
    PostHog.trackEvent(PostHog.EventName.AiIdentification_ItemCreated);
    return item;
  };

  const trackLimitReached = async () => {
    const hash = await fingerprint.get();
    PostHog.trackEvent(PostHog.EventName.AiIdentification_LimitedPopupShown, { fingerprint: hash });
  };

  // Preselect category
  const storeLastCategoryIdIntoRef = (id: CategoryId) => (lastCategoryId.value = id);

  return {
    lookup,
    fetchHistoryList,
    fetchItem,
    createCollectionItemFromIdentification,
    trackLimitReached,

    // Last active category ID
    lastCategoryId,
    storeLastCategoryIdIntoRef,
  };
}
